import type { AssetDataType } from '@repo/types/asset';
import { type DraftAsset, isDraft } from '@repo/types/draft';
import { type OrderAsset, isOrder } from '@repo/types/order';
import { type QuoteAsset, isQuotation } from '@repo/types/quotation';
import { getSource } from '@repo/utils/region.utils';

export const getAssetData = <T, P, Q>(
    orderSelector: (data: OrderAsset) => T,
    draftSelector: (data: DraftAsset) => Q,
    quotationSelector: (data: QuoteAsset) => P
) => {
    return (data?: AssetDataType) => {
        if (!data) {
            return;
        }
        if (isDraft(data)) {
            return draftSelector(data);
        }
        if (isQuotation(data)) {
            return quotationSelector(data);
        }
        if (isOrder(data)) {
            return orderSelector(data);
        }

        // data can not be undefined, that doesn't mean is of draft, quotation or order types
        // in this scenario, return undefined
        return;
    };
};

export const selectAssetDetails = getAssetData(
    (data) => data.assetDetails,
    (data) => data.assetDetails,
    (data) => data.assetDetails
);

export const selectAssetStatus = getAssetData(
    (data) => data.assetDetails.orderStatus,
    (data) => data.assetDetails.status,
    (data) => data.assetDetails.status
);

export const selectAssetCreationDate = getAssetData(
    (data) => data.assetDetails.orderCreationDate,
    (data) => data.assetDetails.createdAt,
    (data) => data.assetDetails.createdAt
);

export const selectCustomerMeetingPoint = getAssetData(
    (data) => data.assetDetails.customerMeetingPointId ?? data.assetDetails.buCode,
    (data) => data.assetDetails.cartData?.customerMeetingPoint?.name,
    (data) => data.assetDetails.cartData?.customerMeetingPoint?.name
);

export const selectCustomerMeetingPointName = getAssetData(
    (data) => data.assetDetails.customerMeetingPointName,
    (data) => data.assetDetails.cartData?.customerMeetingPoint?.name,
    (data) => data.assetDetails.cartData?.customerMeetingPoint?.name
);

export const selectCreatedBy = getAssetData(
    (data) => data.assetDetails.userIdCreated,
    (data) => data.assetDetails.createdBy,
    (data) => data.assetDetails.createdBy
);

export const selectOrderPaymentStatus = getAssetData(
    (data) => data.assetDetails._embedded?.paymentPlans?.[0]?.status,
    (): undefined => undefined,
    (): undefined => undefined
);

export const selectOrderType = getAssetData(
    (data) => data.assetDetails.orderType,
    (): undefined => undefined,
    (): undefined => undefined
);

export const selectAssetAlias = getAssetData(
    (data) => data.assetDetails.orderAlias,
    (data) => data.assetDetails.cartData?.sessionAlias,
    (data) => data.assetDetails.cartData?.sessionAlias
);

export const selectPaymentPlanStatus = getAssetData(
    (data) => data.assetDetails._embedded?.paymentPlans?.[0]?.status,
    (): undefined => undefined,
    (): undefined => undefined
);

export const selectCountryCode = getAssetData(
    (data) => data.assetDetails.countryCode,
    (data) => data.assetDetails.countryCode,
    (data) => data.assetDetails.countryCode
);

export const selectOrderNoSource = getAssetData(
    (data) => data.assetDetails.orderNoSource,
    (_data) => (countryCode: string) => getSource(countryCode),
    (data) => data.assetDetails.quotationId
);

export const selectBuCode = getAssetData(
    (data) => data.assetDetails.buCode ?? '',
    (data) => data.assetDetails.buCode ?? '',
    (data) => data.assetDetails.buCode ?? ''
);

export const selectConnectedOrders = getAssetData(
    (data) => data.assetDetails._embedded?.connectedOrders,
    (): undefined => undefined,
    (): undefined => undefined
);

export const selectCurrencyCode = getAssetData(
    (data) => data.assetDetails.orderCurrencyCode ?? data.assetDetails._embedded?.paymentPlans?.[0]?.currencyCode,
    (data) => data.assetDetails.cartData?.total?.Currency,
    (data) => data.assetDetails.cartData?.total?.Currency
);
